import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTransactionAnalytics } from "@/hooks/use-analytics-sync";
import { useExecutiveClientProfitability } from "@/hooks/use-executive-demo-data";
import { RealtimeStatusIndicator } from "@/components/realtime/realtime-status-indicator";
import { formatSwissCurrency } from "@/integrations/supabase/client";
import { formatDemoCurrency } from "@/lib/demo-data";
import type { TransactionAnalytics } from "@/services/analytics-aggregation";

interface ClientData {
  client: string;
  revenue: string;
  hours: string;
  profitability: number;
  opportunity: string;
  opportunityType: "info" | "warning" | "danger" | "success";
  automationRate?: number;
}

const getProgressColor = (profitability: number) => {
  if (profitability >= 80) return "bg-success";
  if (profitability >= 60) return "bg-warning";
  return "bg-danger";
};

const getOpportunityColor = (type: string) => {
  switch (type) {
    case "info": return "bg-blue-100 text-blue-800";
    case "warning": return "bg-yellow-100 text-yellow-800";
    case "danger": return "bg-red-100 text-red-800";
    case "success": return "bg-green-100 text-green-800";
    default: return "bg-gray-100 text-gray-800";
  }
};

const getOpportunityFromData = (automationRate: number, totalAmount: number, totalTransactions: number): { opportunity: string; type: "info" | "warning" | "danger" | "success" } => {
  if (automationRate < 50) {
    return { opportunity: "Increase Automation", type: "danger" };
  } else if (automationRate < 80) {
    return { opportunity: "Optimize Process", type: "warning" };
  } else if (totalAmount > 200000) {
    return { opportunity: "Expand Mandate", type: "success" };
  } else {
    return { opportunity: "Upsell Services", type: "info" };
  }
};

interface ProfitabilityMatrixProps {
  demoMode?: boolean;
}

export const ProfitabilityMatrix: React.FC<ProfitabilityMatrixProps> = ({ demoMode = true }) => {
  const { data: demoData, isLoading: demoLoading } = useExecutiveClientProfitability();
  const { data: transactionAnalytics, isLoading: realLoading, refresh } = useTransactionAnalytics({ refreshInterval: 60000 });

  const isLoading = demoMode ? demoLoading : realLoading;

  // Generate client data based on demo or real analytics
  const getClientData = (): ClientData[] => {
    // Demo mode: use demo client profitability data
    if (demoMode && demoData) {
      return demoData.map(client => ({
        client: client.name,
        revenue: formatDemoCurrency(client.revenue),
        hours: `${client.hours}h`,
        profitability: client.profitMargin,
        opportunity: client.profitMargin >= 70 ? "Expand Mandate" : 
                     client.profitMargin >= 60 ? "Optimize Process" : "Increase Efficiency",
        opportunityType: client.profitMargin >= 70 ? "success" as const : 
                        client.profitMargin >= 60 ? "info" as const : "warning" as const,
        automationRate: Math.round(85 + Math.random() * 10), // Simulated automation rate
      }));
    }

    // Real mode: use transaction analytics
    if (!transactionAnalytics || transactionAnalytics.length === 0) return [];

    const mandateMap = new Map<string, TransactionAnalytics[]>();
    transactionAnalytics.forEach((analyticsItem) => {
      const existingEntries = mandateMap.get(analyticsItem.mandateId) ?? [];
      existingEntries.push(analyticsItem);
      mandateMap.set(analyticsItem.mandateId, existingEntries);
    });

    return Array.from(mandateMap.entries())
      .slice(0, 4)
      .map<ClientData | null>(([mandateId, analyticsArray], index) => {
        if (analyticsArray.length === 0) {
          return null;
        }

        const totalRevenue = analyticsArray.reduce((sum, analyticsItem) => sum + (analyticsItem.totalAmount || 0), 0);
        const totalTransactions = analyticsArray.reduce((sum, analyticsItem) => sum + (analyticsItem.totalTransactions || 0), 0);

        const totalAutomation = analyticsArray.reduce((sum, analyticsItem) => sum + (analyticsItem.automationRate || 0), 0);
        const avgAutomationRate = analyticsArray.length > 0 ? totalAutomation / analyticsArray.length : 0;

        const profitability = Math.min(Math.round((avgAutomationRate + totalTransactions / 100) * 0.8), 100);
        const estimatedHours = Math.round(totalTransactions * 0.5 + (100 - avgAutomationRate) * 2);

        const { opportunity, type } = getOpportunityFromData(avgAutomationRate, totalRevenue, totalTransactions);
        const clientNames = ["Enterprise Client", "Growth Client", "Startup Client", "Legacy Client"];

        return {
          client: `${clientNames[index]} (${mandateId.slice(0, 8)})`,
          revenue: formatSwissCurrency(totalRevenue, { compact: true }),
          hours: `${estimatedHours}h`,
          profitability,
          opportunity,
          opportunityType: type,
          automationRate: avgAutomationRate,
        };
      })
      .filter((value): value is ClientData => value !== null);
  };

  const clientData = getClientData();

  return (
    <div className="bg-card p-6 rounded-2xl border">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-bold text-brand-primary">Client Profitability Matrix</h3>
          <RealtimeStatusIndicator variant="icon" size="sm" />
          {isLoading && (
            <Badge variant="secondary" className="text-xs animate-pulse">
              Updating...
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            className="btn-swiss-secondary h-7 px-2 py-1.5 rounded-[7px] text-[10px] leading-[10px] hover:bg-brand-secondary transition-colors"
            title="Refresh data"
          >
            <i className="fa-solid fa-refresh" />
          </button>
          <Button variant="outline" size="sm" className="btn-swiss-secondary h-7 text-[10px]">
            <i className="fa-solid fa-sliders mr-1" />
            View Options
          </Button>
        </div>
      </div>

      {clientData.length === 0 ? (
        // Empty state
        <div className="text-center py-8">
          <div className="text-muted-foreground">
            <i className="fa-solid fa-chart-pie fa-2x mb-4" />
            <p className="text-lg font-medium mb-2">No Client Data</p>
            <p className="text-sm">
              Client profitability analysis will appear here once you process transactions from multiple mandates.
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brand-secondary rounded">
              <tr>
                <th className="px-4 py-2 text-left text-[10px] text-muted-foreground font-medium tracking-wider uppercase">
                  Client / Mandate
                </th>
                <th className="px-4 py-2 text-left text-[10px] text-muted-foreground font-medium tracking-wider uppercase">
                  Revenue (YTD)
                </th>
                <th className="px-4 py-2 text-left text-[10px] text-muted-foreground font-medium tracking-wider uppercase">
                  Effort (Hours)
                </th>
                <th className="px-4 py-2 text-left text-[10px] text-muted-foreground font-medium tracking-wider uppercase">
                  Profitability
                </th>
                <th className="px-4 py-2 text-left text-[10px] text-muted-foreground font-medium tracking-wider uppercase">
                  Opportunity
                </th>
              </tr>
            </thead>
            <tbody>
              {clientData.map((client, index) => (
                <tr key={client.client} className={index < clientData.length - 1 ? "border-b border-border" : ""}>
                  <td className="px-4 py-3 whitespace-nowrap text-xs font-medium text-foreground">
                    <div>
                      {client.client}
                      {typeof client.automationRate === 'number' && (
                        <div className="text-xs text-muted-foreground mt-1">
                          {Math.round(client.automationRate)}% automated
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-foreground">
                    {client.revenue}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-foreground">
                    {client.hours}
                  </td>
                  <td className="px-4 py-3">
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className={`${getProgressColor(client.profitability)} h-2 rounded-full transition-all duration-300`}
                        style={{ width: `${client.profitability}%` }}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {client.profitability}%
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-[10px] rounded-full font-medium ${getOpportunityColor(client.opportunityType)}`}>
                      {client.opportunity}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
